import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useParams, useLocation, useOutletContext } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth, isStaffRole, isTournamentManager, staffHomePath } from './contexts/AuthContext';
import LoginPage from './components/Auth/LoginPage';
import ErpShell from './components/ERP/ErpShell';
import RequireTournamentStaff from './components/ERP/OfficialTournaments/RequireTournamentStaff';
import { clubPrimaryColor, useClubPublicConfig, useClubPublicConfigLoaded, useClubPublicConfigPolling } from './services/clubPublicConfig';
import { isModuleOn, isRouteEnabled } from './services/clubModules';
import { buildTheme } from './theme/theme';
import { applyBrandColor } from './theme/appleTokens';
import { getClubStorageItem } from './services/currentClub';
import { LEAGUE_SPACE, getPlatformSpace } from './services/platformSpace';
import './App.css';

// Espace de la plateforme, choisi une fois au démarrage (services/platformSpace.js) : `lfs.<domaine racine>`,
// `lfs.localhost` ou `?space=league` → espace Ligue (ex-MySquash, src/league/) ; tout le reste → espace club (l'ERP,
// ci-dessous, inchangé). L'espace Ligue est un chunk à part, chargé seulement dans la Ligue : le premier affichage de
// l'espace club ne grossit pas.
const PLATFORM_SPACE = getPlatformSpace();
const LeagueApp = React.lazy(() => import('./league/LeagueApp'));

// Pages chargées à la demande (un fichier JS par page) : le premier affichage ne parse plus tout l'ERP
// d'un coup. Une fois connecté, toutes les pages sont préchargées en arrière-plan (usePreloadPages) :
// changer de page n'attend donc jamais un téléchargement.
const PAGE_LOADERS = {
  ErpSeasonMirrorPage: () => import('./components/ERP/ErpSeasonMirrorPage'),
  ErpDbToolsPage: () => import('./components/ERP/ErpDbToolsPage'),
  ErpMatchesHubPage: () => import('./components/ERP/Matches/ErpMatchesHubPage'),
  TournamentBracketPage: () => import('./components/ERP/Matches/TournamentBracketPage'),
  ClassementPrintPage: () => import('./components/ERP/ClassementPrintPage'),
  ErpBoxManagementPage: () => import('./components/ERP/Catalog/ErpBoxManagementPage'),
  TournamentsListPage: () => import('./components/ERP/TournamentsListPage'),
  TournamentDetailPage: () => import('./components/ERP/TournamentDetailPage'),
  NewTournamentPage: () => import('./components/ERP/Tournament/create/NewTournamentPage'),
  MyOfficialTournamentsPage: () => import('./components/ERP/OfficialTournaments/MyOfficialTournamentsPage'),
  ErpInterclubsPage: () => import('./components/ERP/Interclub/ErpInterclubsPage'),
  InterclubDivisionPage: () => import('./components/ERP/Interclub/InterclubDivisionPage'),
  InterclubFixturePage: () => import('./components/ERP/Interclub/InterclubFixturePage'),
  InterclubTeamPage: () => import('./components/ERP/Interclub/InterclubTeamPage'),
  PlayersHubPage: () => import('./components/ERP/PlayersHubPage'),
  PlayerDetailPage: () => import('./components/ERP/Players/PlayerDetailPage'),
  ErpMotwPredictionsPage: () => import('./components/ERP/Motw/ErpMotwPredictionsPage'),
  ErpHomeHighlightsPage: () => import('./components/ERP/HomeHighlights/ErpHomeHighlightsPage'),
  ErpBadgesPage: () => import('./components/ERP/ErpBadgesPage'),
  ErpNotificationsPage: () => import('./components/ERP/ErpNotificationsPage'),
  ErpTerrainsPage: () => import('./components/ERP/Terrains/ErpTerrainsPage'),
  ErpShopPage: () => import('./components/ERP/Shop/ErpShopPage'),
  ErpShopProductPage: () => import('./components/ERP/Shop/ErpShopProductPage'),
  ErpShopOrderPage: () => import('./components/ERP/Shop/ErpShopOrderPage'),
  ErpLightingPage: () => import('./components/ERP/Lighting/ErpLightingPage'),
  ErpConfigurationPage: () => import('./components/ERP/Configuration/ErpConfigurationPage'),
  // Ancienne fiche « formule » : les abonnements sont des articles de la boutique, redirection vers l'article.
  MembershipPlanRedirect: () => import('./components/ERP/Shop/MembershipPlanRedirect'),
  ObsLiveScore: () => import('./components/Screen/ObsLiveScore'),
  MatchLive: () => import('./components/Matches/MatchLive'),
  ScreenDisplay: () => import('./components/Screen/ScreenDisplay'),
  ResetPasswordPage: () => import('./components/Public/ResetPasswordPage'),
  LegalPage: () => import('./components/Public/LegalPage'),
};
const {
  ErpSeasonMirrorPage,
  ErpDbToolsPage,
  ErpMatchesHubPage,
  TournamentBracketPage,
  ClassementPrintPage,
  ErpBoxManagementPage,
  TournamentsListPage,
  TournamentDetailPage,
  NewTournamentPage,
  MyOfficialTournamentsPage,
  ErpInterclubsPage,
  InterclubDivisionPage,
  InterclubFixturePage,
  InterclubTeamPage,
  PlayersHubPage,
  PlayerDetailPage,
  ErpMotwPredictionsPage,
  ErpHomeHighlightsPage,
  ErpBadgesPage,
  ErpNotificationsPage,
  ErpTerrainsPage,
  ErpShopPage,
  ErpShopProductPage,
  ErpShopOrderPage,
  ErpLightingPage,
  ErpConfigurationPage,
  MembershipPlanRedirect,
  ObsLiveScore,
  MatchLive,
  ScreenDisplay,
  ResetPasswordPage,
  LegalPage,
} = Object.fromEntries(Object.entries(PAGE_LOADERS).map(([name, load]) => [name, React.lazy(load)]));

function usePreloadPages(enabled) {
  React.useEffect(() => {
    if (!enabled) return undefined;
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 300));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const id = idle(() => Object.values(PAGE_LOADERS).forEach((load) => load().catch(() => {})));
    return () => cancel(id);
  }, [enabled]);
}
// Cache Emotion en mode « speedy » (insertRule) y compris en développement : par défaut, en dev, Emotion
// ajoute chaque règle CSS comme texte dans une balise <style>, ce qui force le navigateur à recalculer
// les styles de toute la page à chaque nouveau composant — c'était la principale cause des
// « [Violation] 'message' handler took …ms » lors des changements de page.
const emotionCache = createCache({ key: 'css', speedy: true, prepend: true });

const ADVANCED_UNLOCK_STORAGE_KEY = 'squash_erp_advanced_unlocked';

function isAdvancedUnlocked() {
  try {
    return getClubStorageItem(ADVANCED_UNLOCK_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function RequireAdvanced({ children }) {
  const { user } = useAuth();
  if (user?.role === 'admin' && isAdvancedUnlocked()) return children;
  return <Navigate to={staffHomePath(user?.role, false, user)} replace />;
}

/** Admin du club ; `managers` : aussi un gestionnaire de tournois du club (écran Matchs, bracket — la garde
 * RequireTournamentStaff limite déjà ses pages à ses tournois, l'API filtre les données). */
function RequireAdmin({ children, managers = false }) {
  const { user } = useAuth();
  if (user?.role === 'admin') return children;
  if (managers && isTournamentManager(user)) return children;
  return <Navigate to={staffHomePath(user?.role, isAdvancedUnlocked(), user)} replace />;
}

/** Tournois du club : admin en mode gestion ; un gestionnaire n'atteint que la fiche de ses tournois (RequireTournamentStaff). */
function RequireTournamentsAccess({ children }) {
  const { user } = useAuth();
  if (user?.role === 'admin' && isAdvancedUnlocked()) return children;
  if (isTournamentManager(user)) return children;
  return <Navigate to={staffHomePath(user?.role, isAdvancedUnlocked(), user)} replace />;
}

/**
 * Pages d'un module désactivé (Configuration → Modules, voir services/clubModules.js) : retour au tableau de
 * bord. Un staff de tournoi n'a que ses pages de tournois (sinon boucle de redirection) : jamais redirigé.
 */
function RequireEnabledModule() {
  const { user } = useAuth();
  const clubConfig = useClubPublicConfig();
  const location = useLocation();
  // Contexte de l'ErpShell (mode avancé…) transmis tel quel aux pages : sans lui, la Boutique se croit hors mode avancé.
  const shellContext = useOutletContext();
  if (user?.role !== 'tournament_staff' && !isRouteEnabled(clubConfig, location.pathname)) {
    return <Navigate to={staffHomePath(user?.role, isAdvancedUnlocked(), user)} replace />;
  }
  return <Outlet context={shellContext} />;
}

/**
 * Pages publiques d'un module (live score d'un terrain, écran de salle, overlay OBS) : rien n'est monté (aucune
 * requête, aucune connexion SignalR) tant que la configuration publique du club n'est pas chargée, puis un écran
 * « Module désactivé » si la Ligue a coupé le module (un enfant est coupé avec son parent, voir clubModules.js).
 */
function PublicModuleGate({ modules, children }) {
  const clubConfig = useClubPublicConfig();
  const loaded = useClubPublicConfigLoaded();
  if (!loaded) return null;
  if (modules.every((m) => isModuleOn(clubConfig, m))) return children;
  return <ModuleDisabledScreen clubConfig={clubConfig} />;
}

/** Écran « Module désactivé » ; la config est rechargée périodiquement : l'écran repart seul si la Ligue réactive. */
function ModuleDisabledScreen({ clubConfig }) {
  useClubPublicConfigPolling();
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: 24,
        textAlign: 'center',
        // Écrans du club toujours en thème sombre.
        background: '#000000',
        color: '#FFFFFF',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div style={{ fontSize: 28, fontWeight: 600 }}>Module désactivé</div>
      <div style={{ fontSize: 16, opacity: 0.6 }}>Cette page n’est pas disponible pour {clubConfig['club.name'] || 'ce club'}.</div>
    </div>
  );
}

function RedirectObsLiveScoreSlash() {
  const { terrainNumber } = useParams();
  // Conserve la query (`?sport=`, `?matchId=`…) lors de la redirection vers la route canonique.
  const { search } = useLocation();
  return (
    <Navigate
      to={`${terrainNumber != null ? `/obs/live-score/${terrainNumber}` : '/obs/live-score'}${search || ''}`}
      replace
    />
  );
}

/** Mots d'adresse OBS qui ne sont pas un terrain (`/obs/score/3`, `/obs/livescore`…). */
const OBS_PATH_WORDS = new Set(['obs', 'live', 'score', 'scores', 'live-score', 'livescore', 'overlay', 'terrain']);

/**
 * Toute autre adresse sous `/obs/` (`/obs/score`, `/obs/score/3`, `/obs/3`…) : l'overlay, public, au lieu de la
 * connexion du catch-all. Le dernier segment qui n'est pas un mot d'adresse est le terrain (numéro ou code).
 */
function RedirectObsAlias() {
  const { '*': rest } = useParams();
  const { search } = useLocation();
  const terrain = String(rest || '')
    .split('/')
    .map((part) => part.trim())
    .filter((part) => part && !OBS_PATH_WORDS.has(part.toLowerCase()))
    .pop();
  return <Navigate to={`${terrain ? `/obs/live-score/${encodeURIComponent(terrain)}` : '/obs/live-score'}${search || ''}`} replace />;
}

/** Ancienne page Adhésions & crédits (/app/memberships?vue=…) → page Joueurs, même vue (Abonnés par défaut). */
function RedirectToPlayersView() {
  const { search } = useLocation();
  const vue = new URLSearchParams(search).get('vue');
  // Formules : Boutique → Abonnements ; crédits wallet : plus de vue globale (fiche de chaque joueur).
  if (vue === 'formules') return <Navigate to="/app/shop?vue=abonnements" replace />;
  return <Navigate to={vue === 'credits' ? '/app/players' : '/app/players?vue=adherents'} replace />;
}

/** Configuration ; l'ancien onglet Adhésions (formules, `?tab=memberships`) est devenu l'onglet Abonnements de la Boutique. */
function ConfigurationRoute() {
  const { search } = useLocation();
  if (new URLSearchParams(search).get('tab') === 'memberships') return <Navigate to="/app/shop?vue=abonnements" replace />;
  return <ErpConfigurationPage />;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const staffUser = isStaffRole(user?.role) ? user : null;
  const homePath = staffHomePath(staffUser?.role, isAdvancedUnlocked(), staffUser);
  const location = useLocation();
  usePreloadPages(Boolean(staffUser));
  const publicNoAuthWait =
    location.pathname === '/screen' ||
    location.pathname.startsWith('/terrain/') ||
    location.pathname.startsWith('/obs/') ||
    location.pathname === '/cgu';

  if (loading && !publicNoAuthWait) {
    return null;
  }

  return (
    <React.Suspense fallback={null}>
      <Routes>
      {/* Écran de salle et OBS : module broadcast (avec live_scoring) ; live score d'un terrain : live_scoring. */}
      <Route path="/screen" element={<PublicModuleGate modules={['broadcast']}><ScreenDisplay /></PublicModuleGate>} />
      <Route path="/terrain/:terrainNumber" element={<PublicModuleGate modules={['live_scoring']}><MatchLive /></PublicModuleGate>} />
      <Route path="/obs/live-score" element={<PublicModuleGate modules={['broadcast']}><ObsLiveScore /></PublicModuleGate>} />
      <Route path="/obs/live-score/:terrainNumber" element={<PublicModuleGate modules={['broadcast']}><ObsLiveScore /></PublicModuleGate>} />
      <Route path="/obs/live/score" element={<RedirectObsLiveScoreSlash />} />
      <Route path="/obs/live/score/:terrainNumber" element={<RedirectObsLiveScoreSlash />} />
      <Route path="/obs/*" element={<RedirectObsAlias />} />
      <Route path="/login" element={staffUser ? <Navigate to={homePath} replace /> : <LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/cgu" element={<LegalPage />} />
      {/* Plus de page publique de résultats : la racine mène à l'ERP (staff connecté) ou à sa connexion. */}
      <Route path="/" element={<Navigate to={staffUser ? homePath : '/login'} replace />} />
      <Route path="/app" element={staffUser ? <ErpShell /> : <Navigate to="/login" replace />}>
        {/* Staff de tournoi (sans rôle d'admin) : « Mes tournois officiels », et pour un gestionnaire de tournois du club
            l'écran Matchs, le bracket et la fiche de ses tournois (docs/TOURNAMENT_MANAGERS.md). */}
        <Route element={<RequireTournamentStaff />}>
        <Route index element={<Navigate to={homePath} replace />} />
        <Route element={<RequireEnabledModule />}>
          {/* Ancien tableau de bord : celui de la box league est le premier onglet de Box (Compétitions). */}
          <Route path="dashboard" element={<Navigate to="/app/catalog" replace />} />
          <Route
            path="season-mirror"
            element={
              <RequireAdvanced>
                <ErpSeasonMirrorPage />
              </RequireAdvanced>
            }
          />
          <Route
            path="matches"
            element={
              <RequireAdmin managers>
                <ErpMatchesHubPage />
              </RequireAdmin>
            }
          />
          {/* « Voir bracket » de l'onglet Tournoi : tableaux d'un tournoi ou d'un import (?tableau= le tableau affiché). */}
          <Route
            path="matches/bracket/:tournamentId"
            element={
              <RequireAdmin managers>
                <TournamentBracketPage />
              </RequireAdmin>
            }
          />
          <Route
            path="configuration"
            element={
              <RequireAdvanced>
                <ConfigurationRoute />
              </RequireAdvanced>
            }
          />
          <Route
            path="settings"
            element={
              <Navigate to="/app/configuration?tab=league" replace />
            }
          />
          <Route
            path="motw-predictions"
            element={
              <RequireAdvanced>
                <ErpMotwPredictionsPage />
              </RequireAdvanced>
            }
          />
          <Route
            path="home-highlights"
            element={
              <RequireAdvanced>
                <ErpHomeHighlightsPage />
              </RequireAdvanced>
            }
          />
          {/* Replays : page de Configuration (Compétition → Replays). */}
          <Route path="replays" element={<Navigate to="/app/configuration?tab=replays" replace />} />
          <Route
            path="catalog"
            element={
              // Ouverte au mode bar : la page n'y montre que le tableau de bord et la vue joueur de la file d'attente.
              <RequireAdmin>
                <ErpBoxManagementPage />
              </RequireAdmin>
            }
          />
          <Route
            path="tournaments"
            element={
              <RequireTournamentsAccess>
                <TournamentsListPage />
              </RequireTournamentsAccess>
            }
          />
          <Route
            path="tournaments/nouveau"
            element={
              <RequireTournamentsAccess>
                <NewTournamentPage />
              </RequireTournamentsAccess>
            }
          />
          <Route
            path="tournaments/:tournamentId"
            element={
              <RequireTournamentsAccess>
                <TournamentDetailPage />
              </RequireTournamentsAccess>
            }
          />
          {/* Tournois officiels de la Ligue où le compte est responsable ou adjoint (tous clubs organisateurs) ; la fiche
              est la même que celle d'un tournoi du club, droits lus dans `bundle.access`. */}
          <Route path="tournois-officiels" element={<MyOfficialTournamentsPage />} />
          <Route path="tournois-officiels/:tournamentId" element={<TournamentDetailPage />} />
          <Route
            path="interclubs"
            element={
              <RequireAdvanced>
                <ErpInterclubsPage />
              </RequireAdvanced>
            }
          />
          <Route
            path="interclubs/divisions/:divisionId"
            element={
              <RequireAdvanced>
                <InterclubDivisionPage />
              </RequireAdvanced>
            }
          />
          <Route
            path="interclubs/fixtures/:fixtureId"
            element={
              <RequireAdvanced>
                <InterclubFixturePage />
              </RequireAdvanced>
            }
          />
          <Route
            path="interclubs/teams/:teamId"
            element={
              <RequireAdvanced>
                <InterclubTeamPage />
              </RequireAdvanced>
            }
          />
          {/* Affiliations Ligue : plus de page côté club ; la demande se fait depuis la fiche d'un joueur. */}
          <Route path="affiliations" element={<Navigate to="/app/players" replace />} />
          <Route path="club-matches" element={<Navigate to="/app/matches?vue=club" replace />} />
          <Route
            path="players"
            element={
              <RequireAdvanced>
                <PlayersHubPage />
              </RequireAdvanced>
            }
          />
          <Route
            path="players/:playerId"
            element={
              <RequireAdvanced>
                <PlayerDetailPage />
              </RequireAdvanced>
            }
          />
          <Route
            path="badges"
            element={
              <RequireAdvanced>
                <ErpBadgesPage />
              </RequireAdvanced>
            }
          />
          <Route
            path="notifications"
            element={
              <RequireAdvanced>
                <ErpNotificationsPage />
              </RequireAdvanced>
            }
          />
          <Route
            path="classement-print"
            element={
              // Lecture seule, ouverte depuis le tableau de bord Box : accessible au mode bar.
              <RequireAdmin>
                <ClassementPrintPage />
              </RequireAdmin>
            }
          />
          <Route path="waiting-list" element={<Navigate to="/app/catalog?section=attente" replace />} />
          <Route
            path="terrains"
            element={
              <RequireAdmin>
                <ErpTerrainsPage />
              </RequireAdmin>
            }
          />
          <Route
            path="terrains/eclairage"
            element={
              <RequireAdmin>
                <ErpLightingPage />
              </RequireAdmin>
            }
          />
          <Route path="eclairage" element={<Navigate to="/app/terrains/eclairage" replace />} />
          {/* Ancienne page Adhésions & crédits : onglet Abonnés de la page Joueurs (?vue= conservé). */}
          <Route path="memberships" element={<RedirectToPlayersView />} />
          <Route
            path="shop"
            element={
              <RequireAdmin>
                <ErpShopPage />
              </RequireAdmin>
            }
          />
          <Route
            path="shop/articles/:productId"
            element={
              <RequireAdmin>
                <ErpShopProductPage />
              </RequireAdmin>
            }
          />
          <Route
            path="shop/formules/:planId"
            element={
              <RequireAdmin>
                <MembershipPlanRedirect />
              </RequireAdmin>
            }
          />
          <Route
            path="shop/commandes/:orderId"
            element={
              <RequireAdmin>
                <ErpShopOrderPage />
              </RequireAdmin>
            }
          />
          <Route path="payments" element={<Navigate to="/app/shop?vue=paiements" replace />} />
          <Route
            path="db-tools"
            element={
              <RequireAdvanced>
                <ErpDbToolsPage />
              </RequireAdvanced>
            }
          />
        </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to={staffUser ? homePath : '/login'} replace />} />
      </Routes>
    </React.Suspense>
  );
}

function App() {
  if (PLATFORM_SPACE === LEAGUE_SPACE) {
    return (
      <React.Suspense fallback={null}>
        <LeagueApp />
      </React.Suspense>
    );
  }
  return <ClubApp />;
}

/** Espace club : l'ERP du staff, ses pages publiques, TV et OBS. */
function ClubApp() {
  // Couleur de marque du club (`branding.primary_color`) : tokens mutés + thème MUI reconstruit. La couleur en cache
  // (services/clubPublicConfig.js) est déjà appliquée au premier rendu ; on ne reconstruit qu'au changement réel.
  const clubConfig = useClubPublicConfig();
  const brandColor = clubPrimaryColor(clubConfig);
  const theme = React.useMemo(() => {
    applyBrandColor(brandColor);
    return buildTheme();
  }, [brandColor]);
  // Clé = couleur : beaucoup de composants lisent `appleTokens.accent` au rendu (hors contexte de thème) — les pages
  // sont remontées une seule fois quand la couleur change vraiment (jamais si elle est identique au cache).
  const brandKey = brandColor || 'default';

  return (
    <CacheProvider value={emotionCache}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastContainer
        position="top-center"
        autoClose={1000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Router>
        <AuthProvider>
          <AppRoutes key={brandKey} />
        </AuthProvider>
      </Router>
    </ThemeProvider>
    </CacheProvider>
  );
}

export default App;
